import { API, graphqlOperation } from "aws-amplify";
import { eventTypeByActiveByArea } from "../../../graphql/reloadyard/custom-queries";
import { getEventType } from "../../../graphql/queries";

import get from "lodash/get";

import { minValue, number, required } from "react-admin";

import { parseTime } from "../../../utils/dateUtils";
import { logError } from "../../../utils/appUtils";

// Fetch active event types for the current yard, optionally scoped to a single
// workflow (inbound vs outbound). Returns the array so callers can inspect the
// count (e.g. to decide between a single default and a dropdown). Event types
// with no workflow set are treated as "any" — available for both inbound and
// outbound.
export const queryEventTypes = async (workflow) => {
  const searchQuery = {
    reloadYardId: window.appUser?.reloadYardId,
    activeAreaId: { beginsWith: { active: "true" } },
  };
  const results = await API.graphql(
    graphqlOperation(eventTypeByActiveByArea, searchQuery)
  );
  const items = results.data.eventTypeByActiveByArea.items || [];
  return workflow
    ? items.filter((item) => !item.workflow || item.workflow === workflow)
    : items;
};

export const fetchEventTypes = async (setEventTypes, notify, workflow) => {
  try {
    setEventTypes(await queryEventTypes(workflow));
  } catch (error) {
    logError("eventTypes:eventTypeUtils:fetchEvents", error, notify);
  }
};

// Resolve the event type a customer/carrier should schedule against. The
// account-level default wins, but only when it still points to a usable event
// type: present, active, and valid for this workflow. A deleted
// (getEventType => null), deactivated (active !== "true"), or wrong-workflow
// default (e.g. an inbound-only type pinned as the outbound default) must not
// block scheduling or hand back a type whose schedule/capacity rules don't
// apply. Workflow matches the same way queryEventTypes filters — an event type
// with no workflow set counts as "any". When there is no usable default, fall
// back to the yard's workflow-filtered list: a lone match becomes the default,
// multiple leave the Scheduler to show a dropdown, none disables scheduling.
export const resolveDefaultEventType = async (defaultEventTypeId, workflow) => {
  let eventType = null;
  if (defaultEventTypeId) {
    const resp = await API.graphql(
      graphqlOperation(getEventType, { id: defaultEventTypeId })
    );
    const def = resp.data.getEventType;
    if (
      def &&
      def.active === "true" &&
      (!def.workflow || def.workflow === workflow)
    )
      eventType = def;
  }

  if (eventType) return { eventType, canSchedule: true };

  const options = await queryEventTypes(workflow);
  return {
    eventType: options.length === 1 ? options[0] : null,
    canSchedule: options.length > 0,
  };
};

export const validateStartTime = (value, allValues, getSource) => {
  const today = new Date();
  const source = getSource("endDayTime");
  const endDayTime = get(allValues, source, null);

  if (!value || !endDayTime) return undefined;

  // Use `value` for real-time validation of the modified field
  const startEventTime = parseTime(value);
  const endEventTime = parseTime(endDayTime);

  const startDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    startEventTime.hour,
    startEventTime.minute
  );

  const endDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    endEventTime.hour,
    endEventTime.minute
  );

  if (startDate >= endDate) {
    return "Must be earlier than end time";
  }

  return undefined;
};

export const validateEndTime = (value, allValues, getSource) => {
  const today = new Date();
  const source = getSource("startDayTime");
  const startDayTime = get(allValues, source, null);

  if (!value || !startDayTime) return undefined;

  // Use `value` for real-time validation of the modified field
  const startEventTime = parseTime(startDayTime);
  const endEventTime = parseTime(value);

  const startDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    startEventTime.hour,
    startEventTime.minute
  );

  const endDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    endEventTime.hour,
    endEventTime.minute
  );

  if (startDate >= endDate) {
    return "Must be later than start time";
  }

  return undefined;
};

export const validateDurationAndBuffer = (value, allValues) => {
  if (!value || !allValues?.schedule?.workdays) return undefined;

  // Filter selected workdays
  const selectedDays = allValues.schedule.workdays.filter(
    (day) => day.selected
  );

  if (!selectedDays.length) return undefined;

  // Find the workday with the **shortest** available time range
  const shortestDay = selectedDays.reduce(
    (shortest, day) => {
      const startWorkTime = parseTime(day.startDayTime);
      const endWorkTime = parseTime(day.endDayTime);
      const availableMinutes =
        endWorkTime.hour * 60 +
        endWorkTime.minute -
        (startWorkTime.hour * 60 + startWorkTime.minute);

      return availableMinutes < shortest.availableMinutes
        ? { day, availableMinutes, startWorkTime, endWorkTime }
        : shortest;
    },
    {
      day: selectedDays[0], // Initialize with the first workday
      availableMinutes: Infinity, // Start with a high number
      startWorkTime: parseTime(selectedDays[0].startDayTime),
      endWorkTime: parseTime(selectedDays[0].endDayTime),
    }
  );

  // Compute event's total required time
  const totalEventMinutes =
    (allValues.duration || 0) + (allValues.bufferTime || 0);

  return totalEventMinutes > shortestDay.availableMinutes
    ? `Exceeds available hours (${shortestDay.day.name}: ${shortestDay.day.startDayTime} - ${shortestDay.day.endDayTime})`
    : undefined;
};

export const validatePositiveNumber = [
  required(),
  number(),
  minValue(0, "Must be 0 or greater"),
];
